import { booleanish, type Booleanish } from '@aka/vue-kit';
import dayjs, { type Dayjs } from 'dayjs';
import Utc from 'dayjs/plugin/utc';
import Timezone from 'dayjs/plugin/timezone';
import type { MaybeRefOrGetter } from 'vue';

// Extend dayjs with plugins
dayjs.extend(Utc);
dayjs.extend(Timezone);

const DEFAULT_DATE_FORMAT = 'DD MMM, YYYY';
const DEFAULT_TIME_FORMAT = 'HH:mm';

export type NamedDateFormat =
    | 'dashed'
    | 'monthly'
    | 'monthly-full'
    | 'time-short'
    | 'time-short-24'
    | 'monthly at time-short'
    | 'human-full'
    | 'human-monthly'
    | 'monthly at time-short-24'
    | 'human-monthly at time-short'
    | 'military';

export interface UseDateStringOptions {
    date?: MaybeRefOrGetter<string | Date | Dayjs | undefined | null>;
    namedFormat?: MaybeRefOrGetter<NamedDateFormat | undefined>;
    format?: MaybeRefOrGetter<string | undefined>;
    fallback?: MaybeRefOrGetter<string>;
    local?: MaybeRefOrGetter<Booleanish>;
    tzDisplay?: MaybeRefOrGetter<boolean>;
    timeDisplay?: MaybeRefOrGetter<boolean>;
    dateDisplay?: MaybeRefOrGetter<boolean>;
}

export const useDateString = (options: UseDateStringOptions = {}) => {
    const {
        date = null,
        namedFormat = undefined,
        format = undefined,
        fallback = '-',
        local = true,
        tzDisplay = true,
        timeDisplay = true,
        dateDisplay = true,
    } = options;

    // Inject timezone and global details from the app context
    const timezone = inject<Ref<string | undefined | null>>('aka-dt-timezone', ref(undefined));
    const globalDetails = inject<Ref<boolean>>('aka-dt-details', ref(false));

    // Compute preferred format based on user settings
    const preferredFormat = computed(() => {
        const preferencesStore = {
            dateFormat: undefined,
            timeFormat: undefined,
        };
        const { date_format_user_override: dateOverride = false, time_format_user_override: timeOverride = false } = {};
        let { date_format: dateFormat = DEFAULT_DATE_FORMAT, time_format: timeFormat = DEFAULT_TIME_FORMAT } = {};

        if (dateOverride && preferencesStore.dateFormat) {
            dateFormat = preferencesStore.dateFormat;
        }
        if (timeOverride && preferencesStore.timeFormat) {
            timeFormat = preferencesStore.timeFormat;
        }

        if (!toValue(timeDisplay)) {
            return dateFormat || DEFAULT_DATE_FORMAT;
        }

        if (!toValue(dateDisplay)) {
            return timeFormat || DEFAULT_TIME_FORMAT;
        }

        return (dateFormat || DEFAULT_DATE_FORMAT) + '  ' + (timeFormat || DEFAULT_TIME_FORMAT);
    });

    // Format date based on named format
    const doNamedFormat = (format: NamedDateFormat | undefined | null, date: Dayjs): string | null => {
        switch (format) {
            case 'dashed':
                return date.format('YYYY-MM-DD');
            case 'monthly':
                return date.format('DD MMM, YYYY');
            case 'monthly-full':
                return date.format('DD MMMM, YYYY');
            case 'time-short-24':
                return date.format('HH:mm');
            case 'time-short':
                return date.format('hh:mm A');
            case 'human-monthly':
                return date.format('MMMM D, YYYY');
            case 'human-full':
            case 'human-monthly at time-short':
                return date.format('MMMM D, YYYY h:mm A');
            case 'monthly at time-short':
                return `${doNamedFormat('monthly', date)} at ${doNamedFormat('time-short', date)}`;
            case 'monthly at time-short-24': {
                return `${doNamedFormat('monthly', date)} at ${doNamedFormat('time-short-24', date)}`;
            }
            case 'military': {
                return date.format('DD/MM/YYYY HH:mm');
            }
            default:
                return null;
        }
    };

    // Process input date
    const processInput = (dateValue: string | Date | Dayjs | number | undefined | null) => {
        if (dateValue == null) {
            return undefined;
        }
        if (typeof dateValue === 'number') {
            dateValue = dayjs.unix(dateValue);
        }
        let processedDate = dayjs.utc(dateValue);
        if (!processedDate.isValid()) {
            return undefined;
        }
        if (booleanish(toValue(local))) {
            processedDate = processedDate.local();
        } else {
            processedDate = processedDate.utc();
        }
        return processedDate;
    };

    const inputDate = eagerComputed(() => processInput(toValue(date)));

    const formatDate = (dateValue: Dayjs | undefined) => {
        if (!dateValue) {
            return toValue(fallback);
        }

        if (timezone.value && toValue(tzDisplay)) {
            dateValue = dateValue.tz(timezone.value);
        }

        const namedFormatValue = toValue(namedFormat);
        const namedFormatted = doNamedFormat(namedFormatValue, dateValue);
        if (namedFormatted != null) {
            return namedFormatted;
        }

        const formatValue = toValue(format);
        if (formatValue != null) {
            return dateValue.format(formatValue);
        }

        if (preferredFormat.value) {
            return dateValue.format(preferredFormat.value);
        }

        return dateValue.toString();
    };

    const formatInput = (dateValue: string | Date | Dayjs | number | undefined | null) => {
        return formatDate(processInput(dateValue));
    };

    // Compute displayed text
    const displayedText = eagerComputed(() => formatDate(inputDate.value));

    // Compute CSS classes
    const computedClasses = computed(() => {
        const dateValue = toValue(date);
        const processedDate = dayjs.utc(dateValue);
        return [
            'aka-date',
            'items-center',
            'inline-flex',
            {
                fallback: !processedDate.isValid(),
            },
        ];
    });

    return {
        displayedText,
        inputDate,
        computedClasses,
        timezone,
        globalDetails,
        preferredFormat,
        doNamedFormat,
        processInput,
        formatDate,
        formatInput,
    };
};
